import { readonly, ref } from 'vue'
import type { Ref, DeepReadonly } from 'vue'
import api from '@/api/axios'

interface RequestResponse {
  status: number
  data: any
}

interface ErrorResponse {
  errors?: string | Record<string, string | string[]>
  [key: string]: unknown
}

interface UseRequestReturn {
  payload: Ref<unknown>
  getting: DeepReadonly<Ref<boolean>>
  posting: DeepReadonly<Ref<boolean>>
  deleting: DeepReadonly<Ref<boolean>>
  patching: DeepReadonly<Ref<boolean>>
  errors: DeepReadonly<Ref<string | Record<string, string | string[]> | null>>
  globalError: Ref<string | null>
  post: (url: string, data?: unknown) => Promise<boolean>
  get: (url: string) => Promise<boolean>
  patch: (url: string, data?: unknown) => Promise<boolean>
}

export default (): UseRequestReturn => {
  // data
  const getting = ref<boolean>(false)
  const patching = ref<boolean>(false)
  const posting = ref<boolean>(false)
  const deleting = ref<boolean>(false)
  const payload = ref<unknown>(null)
  const errors = ref<string | Record<string, string | string[]> | null>(null)
  const globalError = ref<string | null>(null)

  // methods
  const resetErrors = (): void => {
    globalError.value = null
    errors.value = null
  }

  const get = async (url: string): Promise<boolean> => {
    resetErrors()
    getting.value = true
    let hasSucceeded = true
    try {
      const res = (await api.get(url)) as RequestResponse
      if (res.status.toString().startsWith('2')) {
        payload.value = res.data
      } else {
        const { errors: errorsMsg, ...data } = res.data as ErrorResponse
        errors.value = errorsMsg || null
        payload.value = data
        hasSucceeded = false
      }
    } catch {
      errors.value = "Une erreur inconnue s'est produite"
      hasSucceeded = false
    }
    getting.value = false
    return hasSucceeded
  }

  const post = async (url: string, data?: unknown): Promise<boolean> => {
    resetErrors()
    posting.value = true
    let hasSucceeded = true
    try {
      const res = (await api.post(url, data)) as RequestResponse
      if (res.status.toString().startsWith('2')) {
        payload.value = res.data
      } else {
        const { errors: errorsMsg, ...responseData } = res.data as ErrorResponse
        errors.value = errorsMsg || null
        payload.value = responseData
        hasSucceeded = false
      }
    } catch {
      console.log('error')
      errors.value = "Une erreur inconnue s'est produite"
      hasSucceeded = false
    }
    posting.value = false
    return hasSucceeded
  }

  const patch = async (url: string, data?: unknown): Promise<boolean> => {
    resetErrors()
    patching.value = true
    let hasSucceeded = true
    try {
      const res = (await api.patch(url, data)) as RequestResponse
      if (res.status.toString().startsWith('2')) {
        payload.value = res.data
      } else {
        const { errors: errorsMsg, ...responseData } = res.data as ErrorResponse
        errors.value = errorsMsg || null
        payload.value = responseData
        hasSucceeded = false
      }
    } catch {
      console.log('error')
      errors.value = "Une erreur inconnue s'est produite"
      hasSucceeded = false
    }
    patching.value = false
    return hasSucceeded
  }

  return {
    payload: payload, //notice the readonly here
    getting: readonly(getting), // and here
    posting: readonly(posting), // and here
    deleting: readonly(deleting), // and here
    patching: readonly(patching), // and here
    errors: readonly(errors), // and here
    globalError,
    post,
    get,
    patch,
  }
}

export type { UseRequestReturn, RequestResponse, ErrorResponse }
